import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ACTIVITY_STATUSES, type ActivityStatus } from "@torre/work-hours";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { canShowStaffCosts } from "../../../config/platformFeatures";
import { useCatalog, useSprints } from "../hooks/useActivityData";
import { Link } from "react-router-dom";
import { activityErrorMessage, fetchInsights, fetchSyncStatus, runGithubSync, type InsightsResponse, type SyncStatus } from "../services/activitiesService";
import { STATUS_TONE, formatMinutes, formatMoney, shortDate } from "../lib/format";

// ---------------------------------------------------------------------------
// Dashboard de insights (#1304): qué está parado y desde cuándo, qué venció,
// cómo va el sprint (puntos, horas, costo para dueños) y la velocidad de los
// sprints anteriores. Los datos los arma el servidor (`workHoursActivitiesInsights`).
// ---------------------------------------------------------------------------

const STATUS_COLOR: Record<ActivityStatus, string> = {
  backlog: "#9ca3af",
  todo: "#0ea5e9",
  in_progress: "#8b5cf6",
  review: "#f59e0b",
  blocked: "#ef4444",
  done: "#10b981",
};

const ALL = "__all__";

export default function ActividadesDashboard() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const { catalog } = useCatalog();
  const { sprints, activeSprintId, loading: sprintsLoading } = useSprints();
  const [sprintId, setSprintId] = useState<string | null>(null);
  const [data, setData] = useState<InsightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Estado de la sincronización con GitHub: explica un tablero vacío (sin token,
  // nunca sincronizado, qué repos mira) en vez de dejar la pantalla en blanco.
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    fetchSyncStatus().then(setSync).catch(() => setSync(null));
  }, [reloadKey]);

  const selected = sprintId ?? activeSprintId ?? ALL;

  useEffect(() => {
    if (sprintsLoading) return;
    let alive = true;
    setLoading(true);
    setError(null);
    fetchInsights(selected === ALL ? { all: true } : { sprintId: selected })
      .then((res) => alive && setData(res))
      .catch((err) => alive && setError(activityErrorMessage(err) ?? t("staffActivities.common.error")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [selected, sprintsLoading, t, reloadKey]);

  const statusData = useMemo(() => ACTIVITY_STATUSES.map((s) => ({ status: s, name: t(`staffActivities.status.${s}`), value: data?.byStatus?.[s] ?? 0 })).filter((d) => d.value > 0), [data, t]);
  const typeData = useMemo(() => (data?.byType ?? []).map((d) => ({ name: catalog.find((ty) => ty.key === d.typeKey)?.name ?? d.typeKey, count: d.count, hours: Math.round((d.minutes / 60) * 10) / 10 })), [data, catalog]);
  // Sin costos donde Nómina/Staff están apagadas (WAYAK): regla de nómina.
  const showCost = canShowStaffCosts() && Boolean(data?.sprint?.cost || data?.byAssignee.some((a) => a.cost));
  const totalOpen = statusData.filter((d) => d.status !== "done").reduce((n, d) => n + d.value, 0);
  const tickColor = isDarkMode ? "#9ca3af" : "#4b5563";

  const card = `rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`;
  const h2 = `mb-3 text-sm font-semibold ${tc.text}`;

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15 text-violet-500">
              <i className="ri-dashboard-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{t("staffActivities.dashboard.title")}</h1>
              <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.dashboard.subtitle")}</p>
            </div>
          </div>
          <label className={`flex items-center gap-2 text-xs ${tc.textSecondary}`}>
            {t("staffActivities.dashboard.sprint")}
            <select value={selected} onChange={(e) => setSprintId(e.target.value)} className={`rounded-lg border px-2 py-1.5 text-xs ${tc.border} ${tc.cardBg} ${tc.text}`} aria-label={t("staffActivities.dashboard.sprint")} data-testid="dashboard-sprint">
              <option value={ALL}>{t("staffActivities.dashboard.allOpen")}</option>
              {sprints.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({t(`staffActivities.backlog.${s.status}`)})
                </option>
              ))}
            </select>
          </label>
        </div>

        {loading && <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.common.loading")}</p>}
        {error && <p className="text-sm text-red-500">{error}</p>}

        {data && !loading && totalOpen === 0 && !data.sprint && (
          <div className="mb-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm" data-testid="dashboard-empty">
            <p className="font-semibold text-amber-700 dark:text-amber-400">{t("staffActivities.dashboard.emptyTitle")}</p>
            <p className={`mt-1 ${tc.textSecondary}`}>
              {sync === null
                ? t("staffActivities.dashboard.emptyBody")
                : sync.configured === false
                  ? t("staffActivities.dashboard.emptyNoToken")
                  : !sync.sync?.lastRunAt
                    ? t("staffActivities.dashboard.emptyNever")
                    : t("staffActivities.dashboard.emptySynced", { repos: sync.sync?.repos?.length ?? 0, date: new Date(sync.sync.lastRunAt).toLocaleString("es-MX") })}
            </p>
            {sync?.defaultRepoRule && (
              <p className={`mt-1 text-xs ${tc.textSecondary}`} data-testid="dashboard-empty-rule">
                {sync.defaultRepoRule.keywords.length
                  ? t("staffActivities.dashboard.emptyRule", { orgs: sync.defaultRepoRule.orgs.join(", "), keywords: sync.defaultRepoRule.keywords.join(", ") })
                  : t("staffActivities.dashboard.emptyRuleAll", { orgs: sync.defaultRepoRule.orgs.join(", ") })}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {sync?.canManage && sync.configured !== false && (
                <button
                  type="button"
                  disabled={syncing}
                  onClick={() => {
                    setSyncing(true);
                    runGithubSync()
                      .catch(() => null)
                      .finally(() => {
                        setSyncing(false);
                        setReloadKey((k) => k + 1);
                      });
                  }}
                  className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                  data-testid="dashboard-sync-now"
                >
                  {syncing ? t("staffActivities.backlog.syncing") : t("staffActivities.dashboard.syncNow")}
                </button>
              )}
              <Link to="/gestion-staff/actividades/backlog" className={`rounded-lg border px-3 py-1.5 text-xs ${tc.border} ${tc.text}`}>
                {t("staffActivities.dashboard.goBacklog")}
              </Link>
            </div>
          </div>
        )}
        {data && !loading && (
          <div className="space-y-4" data-testid="dashboard">
            {/* Resumen del sprint */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className={card}>
                <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.open")}</p>
                <p className={`text-2xl font-bold ${tc.text}`}>{totalOpen}</p>
              </div>
              <div className={card}>
                <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.points")}</p>
                <p className={`text-2xl font-bold ${tc.text}`}>{data.sprint ? `${data.sprint.points.done}/${data.sprint.points.total}` : "—"}</p>
                {data.sprint && (
                  <div className={`mt-2 h-1.5 w-full overflow-hidden rounded-full ${isDarkMode ? "bg-dark-border" : "bg-gray-200"}`}>
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${data.sprint.points.total ? Math.min(100, (data.sprint.points.done / data.sprint.points.total) * 100) : 0}%` }} />
                  </div>
                )}
              </div>
              <div className={card}>
                <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.hours")}</p>
                <p className={`text-2xl font-bold ${tc.text}`}>{data.sprint ? formatMinutes(data.sprint.loggedMinutes) : formatMinutes(data.byAssignee.reduce((n, a) => n + a.loggedMinutes, 0))}</p>
                {data.sprint && <p className={`text-[11px] ${tc.textSecondary}`}>{t("staffActivities.dashboard.hoursLine", { logged: formatMinutes(data.sprint.loggedMinutes), estimate: formatMinutes(data.sprint.estimateMinutes) })}</p>}
              </div>
              {showCost && data.sprint?.cost ? (
                <div className={card} data-testid="dashboard-cost">
                  <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.cost")}</p>
                  {data.sprint.cost.byCurrency && Object.keys(data.sprint.cost.byCurrency).length > 0 ? (
                    Object.entries(data.sprint.cost.byCurrency).map(([cur, c]) => (
                      <p key={cur} className={`text-sm font-semibold ${tc.text}`}>
                        {t("staffActivities.dashboard.costLine", { actual: formatMoney(c.actual, cur), estimated: formatMoney(c.estimated, cur) })}
                      </p>
                    ))
                  ) : (
                    <p className={`text-sm font-semibold ${tc.text}`}>{t("staffActivities.dashboard.costLine", { actual: formatMoney(data.sprint.cost.actual), estimated: formatMoney(data.sprint.cost.estimated) })}</p>
                  )}
                </div>
              ) : (
                <div className={card}>
                  <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.stuck")}</p>
                  <p className={`text-2xl font-bold ${data.stuck.length ? "text-amber-500" : tc.text}`}>{data.stuck.length}</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {/* Por estado */}
              <div className={card}>
                <h2 className={h2}>{t("staffActivities.dashboard.byStatus")}</h2>
                {statusData.length === 0 ? (
                  <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.noData")}</p>
                ) : (
                  <>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                            {statusData.map((d) => (
                              <Cell key={d.status} fill={STATUS_COLOR[d.status]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="mt-2 flex flex-wrap gap-2 text-[11px]">
                      {statusData.map((d) => (
                        <li key={d.status} className={`rounded-full px-2 py-0.5 ${STATUS_TONE[d.status]}`}>
                          {d.name}: {d.value}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              {/* Por tipo */}
              <div className={card}>
                <h2 className={h2}>{t("staffActivities.dashboard.byType")}</h2>
                {typeData.length === 0 ? (
                  <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.noData")}</p>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={typeData} layout="vertical" margin={{ left: 8, right: 8 }}>
                        <XAxis type="number" hide />
                        <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: tickColor }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8b5cf6" radius={4} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
              {/* Velocidad */}
              <div className={card}>
                <h2 className={h2}>{t("staffActivities.dashboard.velocity")}</h2>
                {data.velocity.length === 0 ? (
                  <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.noData")}</p>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.velocity} margin={{ left: 8, right: 8 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickColor }} />
                        <YAxis tick={{ fontSize: 11, fill: tickColor }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="pointsDone" fill="#10b981" radius={4} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Parados */}
              <div className={card} data-testid="dashboard-stuck">
                <h2 className={h2}>
                  {t("staffActivities.dashboard.stuck")} <span className={`font-normal ${tc.textSecondary}`}>· {t("staffActivities.dashboard.stuckHint")}</span>
                </h2>
                {data.stuck.length === 0 ? (
                  <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.noStuck")}</p>
                ) : (
                  <ul className="divide-y divide-gray-200 dark:divide-dark-border">
                    {data.stuck.map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className={`truncate ${tc.text}`}>
                            <span className={`mr-2 font-mono text-[11px] ${tc.textSecondary}`}>{s.key}</span>
                            {s.title}
                          </p>
                          <p className={`text-[11px] ${tc.textSecondary}`}>
                            <span className={`mr-2 rounded-full px-1.5 py-0.5 ${STATUS_TONE[s.status]}`}>{t(`staffActivities.status.${s.status}`)}</span>
                            {s.assigneeName ?? t("staffActivities.kanban.unassigned")}
                          </p>
                        </div>
                        <span className="shrink-0 text-xs font-medium text-amber-600">{t("staffActivities.dashboard.stuckSince", { days: s.days, date: shortDate(s.since?.slice(0, 10)) })}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* Vencidos */}
              <div className={card} data-testid="dashboard-overdue">
                <h2 className={h2}>{t("staffActivities.dashboard.overdue")}</h2>
                {data.overdue.length === 0 ? (
                  <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.noOverdue")}</p>
                ) : (
                  <ul className="divide-y divide-gray-200 dark:divide-dark-border">
                    {data.overdue.map((o) => (
                      <li key={o.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className={`truncate ${tc.text}`}>
                            <span className={`mr-2 font-mono text-[11px] ${tc.textSecondary}`}>{o.key}</span>
                            {o.title}
                          </p>
                          <p className={`text-[11px] ${tc.textSecondary}`}>{o.assigneeName ?? t("staffActivities.kanban.unassigned")}</p>
                        </div>
                        <span className="shrink-0 text-xs font-medium text-red-500">{shortDate(o.dueDate)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Por persona */}
            <div className={card} data-testid="dashboard-assignees">
              <h2 className={h2}>{t("staffActivities.dashboard.byAssignee")}</h2>
              {data.byAssignee.length === 0 ? (
                <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.dashboard.noData")}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`text-left text-xs ${tc.textSecondary}`}>
                        <th className="py-1.5 pr-2">{t("staffActivities.kanban.filterPerson")}</th>
                        <th className="py-1.5 pr-2 text-right">{t("staffActivities.dashboard.open")}</th>
                        <th className="py-1.5 pr-2 text-right">{t("staffActivities.dashboard.done")}</th>
                        <th className="py-1.5 pr-2 text-right">{t("staffActivities.dashboard.pointsCol")}</th>
                        <th className="py-1.5 pr-2 text-right">{t("staffActivities.dashboard.hoursCol")}</th>
                        {showCost && <th className="py-1.5 text-right">{t("staffActivities.dashboard.costCol")}</th>}
                      </tr>
                    </thead>
                    <tbody className={tc.text}>
                      {data.byAssignee.map((a) => (
                        <tr key={a.teamMemberId ?? a.name} className="border-t border-gray-200 dark:border-dark-border">
                          <td className="py-1.5 pr-2">{a.name}</td>
                          <td className="py-1.5 pr-2 text-right">{a.open}</td>
                          <td className="py-1.5 pr-2 text-right">{a.done}</td>
                          <td className="py-1.5 pr-2 text-right">{a.points}</td>
                          <td className="py-1.5 pr-2 text-right">{formatMinutes(a.loggedMinutes)}</td>
                          {showCost && <td className="py-1.5 text-right text-emerald-600">{a.cost ? formatMoney(a.cost.actual, a.cost.currency) : "—"}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
